import { useEffect, useRef, useState, type FormEvent } from "react";
import { LEGAL_PAGE_PATHS } from "./LegalPages";
import "./legal-auth.css";

export const TURNSTILE_STAGING_SITE_KEY = "1x00000000000000000000AA";
export const TURNSTILE_STAGING_TOKEN = "XXXX.DUMMY.TOKEN.XXXX";

export interface ConsentCheckboxState {
  termsAccepted: boolean;
  privacyAccepted: boolean;
  ageConfirmed13Plus: boolean;
}

export const EMPTY_CONSENT_STATE: ConsentCheckboxState = {
  termsAccepted: false,
  privacyAccepted: false,
  ageConfirmed13Plus: false,
};

export function isRegistrationConsentComplete(value: ConsentCheckboxState): boolean {
  return value.termsAccepted && value.privacyAccepted && value.ageConfirmed13Plus;
}

export function RegistrationConsentFields({
  value,
  onChange,
}: {
  value: ConsentCheckboxState;
  onChange: (value: ConsentCheckboxState) => void;
}) {
  function change(field: keyof ConsentCheckboxState, checked: boolean) {
    onChange({ ...value, [field]: checked });
  }
  return <fieldset className="consent-fields">
    <legend>登録前の確認</legend>
    <label>
      <input data-testid="consent-terms" type="checkbox" checked={value.termsAccepted} onChange={(event) => change("termsAccepted", event.target.checked)} required />
      <span><a href={LEGAL_PAGE_PATHS.terms} target="_blank" rel="noreferrer">利用規約</a>を読み、同意します</span>
    </label>
    <label>
      <input data-testid="consent-privacy" type="checkbox" checked={value.privacyAccepted} onChange={(event) => change("privacyAccepted", event.target.checked)} required />
      <span><a href={LEGAL_PAGE_PATHS.privacy} target="_blank" rel="noreferrer">プライバシーポリシー</a>を読み、同意します</span>
    </label>
    <label>
      <input data-testid="consent-age" type="checkbox" checked={value.ageConfirmed13Plus} onChange={(event) => change("ageConfirmed13Plus", event.target.checked)} required />
      <span><strong>私は13歳以上です</strong><small>13歳未満の方は登録できません。18歳未満の方は保護者と<a href={LEGAL_PAGE_PATHS.minors} target="_blank" rel="noreferrer">未成年の利用方針</a>を確認してください。</small></span>
    </label>
  </fieldset>;
}

interface TurnstileApi {
  render(container: HTMLElement, options: {
    sitekey: string;
    action?: string;
    theme?: "auto" | "light" | "dark";
    callback: (token: string) => void;
    "expired-callback": () => void;
    "error-callback": () => void;
  }): string;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let turnstileScript: Promise<void> | undefined;

function loadTurnstile(): Promise<void> {
  if (window.turnstile) return Promise.resolve();
  if (turnstileScript) return turnstileScript;
  turnstileScript = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-shiko-turnstile="true"]');
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("安全確認を読み込めませんでした")), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.dataset.shikoTurnstile = "true";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("安全確認を読み込めませんでした"));
    document.head.appendChild(script);
  });
  return turnstileScript;
}

export function TurnstileWidget({
  siteKey,
  action,
  onToken,
  onError,
}: {
  siteKey: string;
  action: "signup" | "password_reset" | "report";
  onToken: (token: string | null) => void;
  onError?: (message: string) => void;
}) {
  const container = useRef<HTMLDivElement>(null);
  const callbacks = useRef({ onToken, onError });
  callbacks.current = { onToken, onError };

  useEffect(() => {
    let cancelled = false;
    let widgetId: string | undefined;
    callbacks.current.onToken(null);
    void loadTurnstile().then(() => {
      if (cancelled || !container.current || !window.turnstile) return;
      widgetId = window.turnstile.render(container.current, {
        sitekey: siteKey,
        action,
        theme: "auto",
        callback: (token) => callbacks.current.onToken(token),
        "expired-callback": () => callbacks.current.onToken(null),
        "error-callback": () => {
          callbacks.current.onToken(null);
          callbacks.current.onError?.("安全確認に失敗しました。もう一度お試しください");
        },
      });
    }).catch((error: unknown) => {
      if (siteKey === TURNSTILE_STAGING_SITE_KEY && container.current) {
        const input = document.createElement("input"); input.type = "hidden"; input.name = "cf-turnstile-response"; input.value = TURNSTILE_STAGING_TOKEN; container.current.appendChild(input);
        callbacks.current.onToken(TURNSTILE_STAGING_TOKEN);
        return;
      }
      callbacks.current.onError?.(error instanceof Error ? error.message : "安全確認を読み込めませんでした");
    });
    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [siteKey, action]);
  return <div className="turnstile-slot" ref={container} aria-label="自動操作ではないことの確認" />;
}

type SecurityNotice = { kind: "success" | "error"; message: string } | null;

function SecurityNoticeBar({ notice }: { notice: SecurityNotice }) {
  if (!notice) return null;
  return <p className={`security-notice ${notice.kind}`} role="status">{notice.message}</p>;
}

export function PasswordResetRequestForm({ siteKey }: { siteKey?: string }) {
  const [email, setEmail] = useState("");
  const [token, setToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<SecurityNotice>(null);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true); setNotice(null);
    try {
      if (siteKey && !token) throw new Error("安全確認を完了してください");
      const response = await fetch("/api/auth/request-password-reset", {
        method: "POST",
        headers: { "content-type": "application/json", ...(token ? { "x-turnstile-token": token } : {}) },
        body: JSON.stringify({ email, redirectTo: `${window.location.origin}/reset-password` }),
      });
      if (!response.ok) throw new Error("再設定メールを受け付けられませんでした");
      setNotice({ kind: "success", message: "登録済みの場合は、再設定メールが届きます。受信箱をご確認ください。" });
    } catch (error) {
      setNotice({ kind: "error", message: error instanceof Error ? error.message : "処理に失敗しました" });
    } finally { setBusy(false); }
  }
  return <form className="security-form" onSubmit={submit}>
    <h2>パスワードを忘れた方</h2>
    <p>登録済みかどうかにかかわらず、同じ案内を表示します。</p>
    <SecurityNoticeBar notice={notice} />
    <label>メールアドレス<input type="email" autoComplete="email" value={email} onChange={(event) => setEmail(event.target.value)} required /></label>
    {siteKey && <TurnstileWidget siteKey={siteKey} action="password_reset" onToken={setToken} onError={(message) => setNotice({ kind: "error", message })} />}
    <button className="primary wide" disabled={busy}>{busy ? "送信中…" : "再設定メールを送る"}</button>
  </form>;
}

export function PasswordResetForm({ token, onComplete }: { token: string; onComplete?: () => void }) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<SecurityNotice>(null);
  async function submit(event: FormEvent) {
    event.preventDefault(); setNotice(null);
    if (password !== confirmation) {
      setNotice({ kind: "error", message: "確認用パスワードが一致しません" });
      return;
    }
    setBusy(true);
    try {
      const response = await fetch("/api/auth/reset-password", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ token, newPassword: password }),
      });
      if (!response.ok) throw new Error("リンクが無効か、有効期限が切れています");
      setNotice({ kind: "success", message: "パスワードを変更しました。すべての既存セッションを終了します。" });
      onComplete?.();
    } catch (error) {
      setNotice({ kind: "error", message: error instanceof Error ? error.message : "変更に失敗しました" });
    } finally { setBusy(false); }
  }
  return <form className="security-form" onSubmit={submit}>
    <h2>新しいパスワード</h2>
    <SecurityNoticeBar notice={notice} />
    <label>新しいパスワード<input type="password" minLength={8} maxLength={128} autoComplete="new-password" value={password} onChange={(event) => setPassword(event.target.value)} required /></label>
    <label>もう一度入力<input type="password" minLength={8} maxLength={128} autoComplete="new-password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} required /></label>
    <button className="primary wide" disabled={busy}>{busy ? "変更中…" : "パスワードを変更"}</button>
  </form>;
}

export function EmailVerificationNotice({ email }: { email: string }) {
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<SecurityNotice>(null);
  async function resend() {
    setBusy(true); setNotice(null);
    try {
      const response = await fetch("/api/auth/send-verification-email", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, callbackURL: "/" }),
      });
      if (!response.ok) throw new Error("確認メールを送信できませんでした");
      setNotice({ kind: "success", message: "確認メールを送信しました" });
    } catch (error) {
      setNotice({ kind: "error", message: error instanceof Error ? error.message : "送信に失敗しました" });
    } finally { setBusy(false); }
  }
  return <section className="verification-notice">
    <strong>メールアドレスの確認が必要です</strong>
    <p>{email} 宛てのリンクを開くと利用を開始できます。</p>
    <SecurityNoticeBar notice={notice} />
    <button type="button" className="secondary" onClick={() => void resend()} disabled={busy}>{busy ? "送信中…" : "確認メールを再送"}</button>
  </section>;
}
